import { cn } from "@d12labs/utils";
import type * as React from "react";
import { Button } from "./button";

/**
 * Prev/Next state for a 1-based pager. `pageCount < 1` (an empty list) clamps
 * to 1, so it reads "page 1 of 1" with both directions disabled.
 */
export function pagerState(page: number, pageCount: number) {
	const last = Math.max(1, pageCount);
	return {
		canPrev: page > 1,
		canNext: page < last,
		prevPage: Math.max(1, page - 1),
		nextPage: Math.min(last, page + 1),
	};
}

export type PagerProps = {
	/** Current page, 1-based. Convert 0-based state at the call site. */
	page: number;
	pageCount: number;
	onPageChange: (page: number) => void;
	/** Left-hand summary (e.g. "12 activities · page 1/3") — each site keeps its wording. */
	label?: React.ReactNode;
	variant?: "outline" | "ghost" | "secondary";
	className?: string;
};

/** Shared Previous/Next pager: optional summary on the left, buttons on the right. */
export function Pager({
	page,
	pageCount,
	onPageChange,
	label,
	variant = "outline",
	className,
}: PagerProps) {
	const state = pagerState(page, pageCount);
	return (
		<div className={cn("flex items-center justify-between gap-2", className)}>
			{label ?? <span />}
			<div className="flex gap-2">
				<Button
					variant={variant}
					size="sm"
					aria-label="Previous page"
					disabled={!state.canPrev}
					onClick={() => onPageChange(state.prevPage)}
				>
					Previous
				</Button>
				<Button
					variant={variant}
					size="sm"
					aria-label="Next page"
					disabled={!state.canNext}
					onClick={() => onPageChange(state.nextPage)}
				>
					Next
				</Button>
			</div>
		</div>
	);
}
